import React, { useEffect, useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import PaywallNotice from "@/components/PaywallNotice";
import { 
  TrendingUp, BarChart3, Target, BookOpen, Award, 
  AlertCircle, Filter, Calendar, Sparkles,
  Loader2, TrendingDown, Minus, ArrowLeft, Lock
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const PYQPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState("ALL");
  const [selectedYear, setSelectedYear] = useState("10");
  
  // Topic search state
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [years, setYears] = useState(10);
  const [result, setResult] = useState(null);

  const isPro = user?.role === "PRO";

  useEffect(() => {
    const load = async () => {
      try {
        const { data: res } = await managedAxios.get(`${API_BASE}/pyq/overview`);
        setData(res);
      } catch (err) {
        const msg = err?.response?.data?.detail;
        if (err?.response?.status === 403 && msg) {
          setPaywall(true);
          toast({ variant: "destructive", description: msg });
        } else {
          toast({ variant: "destructive", description: msg || "Could not load PYQ overview." });
        }
      }
    };
    load();
  }, [toast]);

  // Mock comprehensive PYQ data (in real app, this would come from backend)
  const comprehensiveData = {
    totalQuestions: 850,
    yearsAnalyzed: 25,
    lastUpdated: "December 2024",
    papers: {
      GS1: {
        totalQuestions: 210,
        avgQuestionsPerYear: 8.4,
        trend: "Stable",
        topics: [
          { name: "Indian History", count: 58, percentage: 27.6, priority: "High" },
          { name: "Art & Culture", count: 45, percentage: 21.4, priority: "High" },
          { name: "Geography", count: 42, percentage: 20.0, priority: "Medium" },
          { name: "Society", count: 38, percentage: 18.1, priority: "Medium" },
          { name: "World History", count: 27, percentage: 12.9, priority: "Low" }
        ]
      },
      GS2: {
        totalQuestions: 220,
        avgQuestionsPerYear: 8.8,
        trend: "Rising",
        topics: [
          { name: "Polity & Constitution", count: 75, percentage: 34.1, priority: "High" },
          { name: "Governance", count: 52, percentage: 23.6, priority: "High" },
          { name: "International Relations", count: 48, percentage: 21.8, priority: "High" },
          { name: "Social Justice", count: 30, percentage: 13.6, priority: "Medium" },
          { name: "Statutory Bodies", count: 15, percentage: 6.8, priority: "Low" }
        ]
      },
      GS3: {
        totalQuestions: 230,
        avgQuestionsPerYear: 9.2,
        trend: "High Variability",
        topics: [
          { name: "Economy", count: 68, percentage: 29.6, priority: "High" },
          { name: "Environment", count: 55, percentage: 23.9, priority: "High" },
          { name: "Science & Technology", count: 48, percentage: 20.9, priority: "High" },
          { name: "Security", count: 35, percentage: 15.2, priority: "Medium" },
          { name: "Disaster Management", count: 24, percentage: 10.4, priority: "Low" }
        ]
      },
      GS4: {
        totalQuestions: 190,
        avgQuestionsPerYear: 7.6,
        trend: "Stable",
        topics: [
          { name: "Ethics & Integrity", count: 72, percentage: 37.9, priority: "High" },
          { name: "Case Studies", count: 58, percentage: 30.5, priority: "High" },
          { name: "Aptitude", count: 35, percentage: 18.4, priority: "Medium" },
          { name: "Public Service Values", count: 25, percentage: 13.2, priority: "Medium" }
        ]
      }
    },
    yearWiseDistribution: [
      { year: "2024", gs1: 9, gs2: 10, gs3: 11, gs4: 8 },
      { year: "2023", gs1: 8, gs2: 9, gs3: 10, gs4: 7 },
      { year: "2022", gs1: 9, gs2: 8, gs3: 9, gs4: 8 },
      { year: "2021", gs1: 8, gs2: 9, gs3: 10, gs4: 7 },
      { year: "2020", gs1: 7, gs2: 8, gs3: 8, gs4: 8 }
    ],
    predictedTopics: [
      { topic: "Climate Change & Net Zero", paper: "GS3", probability: 92, reason: "Recent COP summits + Budget focus" },
      { topic: "Cooperative Federalism", paper: "GS2", probability: 88, reason: "Supreme Court rulings + GST Council dynamics" },
      { topic: "Digital Public Infrastructure", paper: "GS3", probability: 85, reason: "India Stack success + G20 presidency" },
      { topic: "Constitutional Morality", paper: "GS2", probability: 82, reason: "Recent SC judgments citing constitutional morality" },
      { topic: "Circular Economy", paper: "GS3", probability: 78, reason: "Government policy push + SDG alignment" }
    ],
    recentTrends: [
      "Increasing focus on contemporary issues over traditional topics",
      "More integration across GS papers (e.g., polity + economy)",
      "Growing emphasis on case study-based questions in GS4",
      "Application-oriented questions replacing factual recall",
      "Indian context and examples mandatory in answers"
    ]
  };

  const handleAnalyze = async () => {
    if (!topic.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a topic"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/pyq/analyze`,
        {
          topic,
          years: isPro ? years : 10  // FREE users limited to 10 years
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: `Analyzed ${isPro ? years : 10}-year PYQ trends for ${topic}`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to analyze PYQs"
      });
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend?.includes("↑") || trend?.toLowerCase().includes("increas") || trend?.toLowerCase().includes("rising")) {
      return <TrendingUp className="w-5 h-5 text-green-600" />;
    } else if (trend?.includes("↓") || trend?.toLowerCase().includes("decreas")) {
      return <TrendingDown className="w-5 h-5 text-red-600" />;
    }
    return <Minus className="w-5 h-5 text-slate-600" />;
  };

  const selectedPaperData = selectedPaper === "ALL" ? null : comprehensiveData.papers[selectedPaper];

  return (
    <div className="min-h-screen bg-slate-50/30" data-testid="pyq-page">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-indigo-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-10 space-y-8">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header - Premium Glassmorphism */}
      <div className="glass-card rounded-2xl p-6 md:p-8 animate-fade-up">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-1">
              {isPro ? (
                <Badge className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  PRO
                </Badge>
              ) : (
                <Badge className="px-3 py-1 bg-slate-100 text-slate-700 border-0">
                  FREE
                </Badge>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              PYQ Analyzer
            </h1>
            <p className="text-slate-600">
              {isPro 
                ? "30-year comprehensive analysis • Topic weightage • Trend prediction • Practice MCQs"
                : "10-year analysis • Sample PYQs • Basic insights"
              }
            </p>
          </div>
          {!isPro && (
            <Button
              onClick={() => navigate('/upgrade')}
              size="sm"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs"
            >
              <Lock className="w-3 h-3 mr-2" />
              Upgrade for Full Analysis
            </Button>
          )}
        </div>
      </div>

      {/* Topic Search Section */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">Search Specific Topic</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="topic">Topic</Label>
              <Input
                id="topic"
                placeholder="e.g., Fundamental Rights, Monetary Policy, Climate Change"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="years">Analysis Period</Label>
              <select
                id="years"
                value={years}
                onChange={(e) => setYears(parseInt(e.target.value))}
                disabled={loading || !isPro}
                className="w-full px-3 py-2 border border-slate-300 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="10">Last 10 Years</option>
                <option value="20">Last 20 Years {!isPro && "(PRO)"}</option>
                <option value="30">Last 30 Years {!isPro && "(PRO)"}</option>
              </select>
              {!isPro && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Extended period is a PRO feature
                </p>
              )}
            </div>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={loading}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <BarChart3 className="w-4 h-4 mr-2" />
                Analyze PYQ Trends
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Topic Search Results */}
      {result && (
        <div className="space-y-4">
          {/* FREE Version: Sample PYQ and Basic Info */}
          {!isPro && (
            <>
              {result.pyq_sample && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900">Sample PYQ</h3>
                      <Badge variant="outline">{result.pyq_sample.year}</Badge>
                    </div>
                    <p className="text-sm text-slate-700">{result.pyq_sample.question}</p>
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold">Answer:</span> {result.pyq_sample.answer}
                    </p>
                    {result.insight && (
                      <div className="pt-3 border-t border-blue-200">
                        <p className="text-sm text-blue-900 font-semibold mb-1">Insight:</p>
                        <p className="text-sm text-slate-700">{result.insight}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Basic stats for FREE users */}
              {(result.trend || result.weightage_percent) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {result.trend && (
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-600 mb-1">Trend Direction</p>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(result.trend)}
                          <p className="text-lg font-bold text-slate-900">{result.trend}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {result.weightage_percent !== undefined && (
                    <Card className="border-l-4 border-l-indigo-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-600 mb-1">Weightage</p>
                        <p className="text-2xl font-bold text-indigo-600">
                          {result.weightage_percent}%
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Upgrade Prompt for FREE Users */}
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-2">Unlock Full PYQ Analysis</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Upgrade to PRO for 30-year analysis, frequency trends, subtopic mapping, 
                        AI-predicted MCQs, strategic insights, and year-wise breakdowns.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="outline" className="text-xs">30-Year Analysis</Badge>
                        <Badge variant="outline" className="text-xs">Frequency Trends</Badge>
                        <Badge variant="outline" className="text-xs">Practice MCQs</Badge>
                        <Badge variant="outline" className="text-xs">Strategic Insights</Badge>
                        <Badge variant="outline" className="text-xs">Previous PYQs</Badge>
                      </div>
                      <Button
                        onClick={() => navigate('/upgrade')}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      >
                        Upgrade to PRO
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* PRO Version: Comprehensive Analysis */}
          {isPro && (
            <>
              {/* Overview Cards */}
              {(result.trend || result.weightage_percent || result.frequency) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {result.trend && (
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-600 mb-1">Trend Direction</p>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(result.trend)}
                          <p className="text-lg font-bold text-slate-900">{result.trend}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {result.weightage_percent !== undefined && (
                    <Card className="border-l-4 border-l-indigo-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-600 mb-1">Weightage</p>
                        <p className="text-2xl font-bold text-indigo-600">
                          {result.weightage_percent}%
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {result.frequency && (
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-600 mb-1">Total Questions ({years}Y)</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {result.frequency[`${years}_years`] || 0}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Frequency Breakdown */}
              {result.frequency && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Frequency Analysis</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-xs text-slate-600 mb-1">Last 10 Years</p>
                        <p className="text-3xl font-bold text-green-600">
                          {result.frequency["10_years"] || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-600 mb-1">Last 20 Years</p>
                        <p className="text-3xl font-bold text-blue-600">
                          {result.frequency["20_years"] || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-600 mb-1">Last 30 Years</p>
                        <p className="text-3xl font-bold text-purple-600">
                          {result.frequency["30_years"] || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Topic Mapping */}
              {result.topic_mapping && result.topic_mapping.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-3">Subtopics Asked</h3>
                    <div className="flex flex-wrap gap-2">
                      {result.topic_mapping.map((subtopic, idx) => (
                        <Badge key={idx} variant="secondary">
                          {subtopic}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Previous UPSC Prelims Questions */}
              {result.previous_pyqs && result.previous_pyqs.length > 0 && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Previous UPSC Prelims Questions ({result.previous_pyqs.length})
                    </h3>
                    <div className="space-y-4">
                      {result.previous_pyqs.map((pyq, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="bg-blue-600 text-white text-xs">
                              UPSC {pyq.year}
                            </Badge>
                            <span className="text-xs text-slate-500">Q{idx + 1}</span>
                          </div>
                          <p className="text-sm text-slate-900 mb-3">{pyq.question}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-blue-700">
                              Answer: {pyq.correct_answer}
                            </span>
                          </div>
                          {pyq.explanation && (
                            <p className="text-xs text-slate-600 mt-2 italic border-t border-blue-100 pt-2">
                              {pyq.explanation}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default PYQPage;
